import React, { useState, useRef, useEffect, useCallback } from 'react';
import { NamedLink } from '../../components';
import { createResourceLocatorString } from '../../util/routes';
import SearchMap from '../SearchPage/SearchMap/SearchMap';
import { readMapViewport, writeMapViewport } from './mapViewport';
import {
  boundsFromPins,
  CLUSTER_BUBBLE_BASE,
  PIN_STYLE,
  WRAPPER_STYLE,
  MAP_PIN_SVG,
} from './mapConstants';
import { getScWorker, getLoadedPinKey, setLoadedPinKey } from './mapWorkerSingleton';
import type { MapPin, HoveredPinData } from './types';
import css from './BrowsePage.module.css';

interface MapViewProps {
  pins: MapPin[];
  routeConfiguration: any;
  history: any;
  intl: any;
}

const MapView = ({ pins, routeConfiguration, history, intl }: MapViewProps) => {
  const markersRef = useRef<any[]>([]);
  const sectionRef = useRef<HTMLElement | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scaledPinRef = useRef<HTMLDivElement | null>(null);
  const workerRef = useRef<Worker | null>(null);
  const reqIdRef = useRef(0);

  // Keep latest props in refs so worker callbacks never have stale closures
  const routeConfigRef = useRef(routeConfiguration);
  const historyRef = useRef(history);
  routeConfigRef.current = routeConfiguration;
  historyRef.current = history;

  const [hoveredPin, setHoveredPin] = useState<HoveredPinData | null>(null);

  const scheduleHide = useCallback(() => {
    hideTimerRef.current = setTimeout(() => {
      setHoveredPin(null);
      if (scaledPinRef.current) {
        scaledPinRef.current.style.transform = '';
        scaledPinRef.current = null;
      }
    }, 150);
  }, []);

  const cancelHide = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  // drawMarkers: called by worker.onmessage with cluster results.
  // Stored as a ref so the worker lifecycle effect (empty deps) can always call
  // the latest version without being re-created.
  const drawMarkers = useCallback(
    (clusters: any[]) => {
      const map = (window as any).mapboxMap;
      if (!map || !(window as any).mapboxgl) return;

      markersRef.current.forEach(m => m.remove());
      markersRef.current = [];

      clusters.forEach(feature => {
        const [lng, lat] = feature.geometry.coordinates;
        const { cluster, point_count, cluster_id, id, slug, title, imgUrl, formattedPrice } =
          feature.properties;

        const wrapper = document.createElement('div');
        wrapper.style.cssText = WRAPPER_STYLE;

        if (cluster) {
          const size = Math.round(Math.min(64, 36 + Math.log2(point_count) * 5));
          const bubble = document.createElement('div');
          bubble.style.cssText = `width:${size}px;height:${size}px;${CLUSTER_BUBBLE_BASE}`;
          bubble.textContent = point_count > 999 ? '999+' : String(point_count);
          wrapper.appendChild(bubble);
          wrapper.addEventListener('mouseenter', () => { bubble.style.transform = 'scale(1.12)'; });
          wrapper.addEventListener('mouseleave', () => { bubble.style.transform = ''; });
          wrapper.addEventListener('click', () => {
            workerRef.current?.postMessage({ type: 'expand', clusterId: cluster_id, lng, lat });
          });
          markersRef.current.push(
            new (window as any).mapboxgl.Marker(wrapper, { anchor: 'center' }).setLngLat([lng, lat]).addTo(map)
          );
        } else {
          const pin = document.createElement('div');
          pin.style.cssText = PIN_STYLE;
          pin.innerHTML = MAP_PIN_SVG;
          wrapper.appendChild(pin);
          wrapper.dataset.pin = 'true';
          wrapper.addEventListener('mouseenter', () => {
            cancelHide();
            if (scaledPinRef.current && scaledPinRef.current !== pin) {
              scaledPinRef.current.style.transform = '';
            }
            scaledPinRef.current = pin;
            pin.style.transform = 'scale(1.15)';
            const rect = wrapper.getBoundingClientRect();
            const sr = sectionRef.current?.getBoundingClientRect() || { top: 0, left: 0 };
            const pinX = rect.left - sr.left + rect.width / 2;
            const pinTipY = rect.bottom - sr.top;
            setHoveredPin({ imgUrl, formattedPrice, title, pinX, pinTipY, id, slug });
          });
          wrapper.addEventListener('mouseleave', (e: MouseEvent) => {
            if (cardRef.current?.contains(e.relatedTarget as Node)) return;
            scheduleHide();
          });
          wrapper.addEventListener('click', () => {
            historyRef.current.push(
              createResourceLocatorString('ListingPage', routeConfigRef.current, { id, slug }, {})
            );
          });
          markersRef.current.push(
            new (window as any).mapboxgl.Marker(wrapper, { anchor: 'bottom' }).setLngLat([lng, lat]).addTo(map)
          );
        }
      });
    },
    [scheduleHide, cancelHide]
  );
  const drawMarkersRef = useRef(drawMarkers);
  drawMarkersRef.current = drawMarkers;

  // Send getClusters for the current map viewport
  const requestClusters = useCallback(() => {
    const map = (window as any).mapboxMap;
    if (!map || !workerRef.current) return;
    const b = map.getBounds();
    const zoom = Math.floor(map.getZoom());
    const reqId = ++reqIdRef.current;
    workerRef.current.postMessage({
      type: 'getClusters',
      bbox: [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()],
      zoom,
      reqId,
    });
  }, []);

  // Activate map controls + restore bookmarked viewport (run once on mount)
  useEffect(() => {
    const viewport = readMapViewport();
    const activate = () => {
      if (!(window as any).mapboxMap) return false;
      const map = (window as any).mapboxMap;
      map.scrollZoom.enable();
      map.dragPan.enable();
      map.resize();
      if (viewport) map.jumpTo({ center: [viewport.lng, viewport.lat], zoom: viewport.zoom });
      return true;
    };
    if (!activate()) {
      const t = setTimeout(activate, 800);
      return () => clearTimeout(t);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Write viewport to URL on every moveend (replaceState: no history entry, no re-render)
  useEffect(() => {
    let map: any = null;
    const onMoveEnd = () => {
      if (!map) return;
      const c = map.getCenter();
      writeMapViewport({ lat: c.lat, lng: c.lng, zoom: map.getZoom() });
    };
    const init = () => {
      if (!(window as any).mapboxMap) return false;
      map = (window as any).mapboxMap;
      map.on('moveend', onMoveEnd);
      return true;
    };
    if (!init()) {
      const t = setTimeout(init, 800);
      return () => { clearTimeout(t); if (map) map.off('moveend', onMoveEnd); };
    }
    return () => { if (map) map.off('moveend', onMoveEnd); };
  }, []);

  // Attach to the module-level singleton worker and wire message handler.
  // The worker is NOT terminated on unmount: the index stays warm.
  useEffect(() => {
    const worker = getScWorker();
    if (!worker) return;
    workerRef.current = worker;

    worker.onmessage = ({ data }: MessageEvent) => {
      switch (data.type) {
        case 'ready':
          requestClusters();
          break;
        case 'clusters':
          if (data.reqId === reqIdRef.current) {
            drawMarkersRef.current?.(data.clusters);
          }
          break;
        case 'expansion':
          (window as any).mapboxMap?.easeTo({
            center: [data.lng, data.lat],
            zoom: Math.min(data.zoom, 20),
          });
          break;
        default:
          break;
      }
    };

    // If the worker already has pins loaded, just request clusters immediately
    if (getLoadedPinKey() && (window as any).mapboxMap) {
      requestClusters();
    }

    return () => { workerRef.current = null; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!workerRef.current || pins.length === 0) return;
    const key = `${pins.length}:${pins.map(p => p.id).join(',')}`;
    if (key === getLoadedPinKey()) return;
    setLoadedPinKey(key);
    workerRef.current.postMessage({ type: 'load', pins });
  }, [pins]);

  // Map moveend → request new clusters; also triggers initial render once map is ready
  useEffect(() => {
    let map: any = null;
    let delayTimer: ReturnType<typeof setTimeout> | null = null;
    const onMoveEnd = () => requestClusters();

    const tryInit = () => {
      if (!(window as any).mapboxMap || !(window as any).mapboxgl) return false;
      map = (window as any).mapboxMap;
      requestClusters();
      map.on('moveend', onMoveEnd);
      return true;
    };

    if (!tryInit()) delayTimer = setTimeout(tryInit, 1000);

    return () => {
      if (delayTimer) clearTimeout(delayTimer);
      if (map) map.off('moveend', onMoveEnd);
      markersRef.current.forEach(m => m.remove());
      markersRef.current = [];
    };
  }, [requestClusters]);

  const CARD_H = hoveredPin?.imgUrl ? 200 : 80;
  const CARD_GAP = 0;

  return (
    <section
      ref={sectionRef}
      className="relative rounded-xl overflow-hidden border border-border h-[600px] shadow-sm"
    >
      {hoveredPin && (
        <div
          ref={cardRef}
          className="absolute z-50 w-48 pointer-events-auto"
          onMouseEnter={cancelHide}
          onMouseLeave={(e: React.MouseEvent) => {
            if ((e.relatedTarget as HTMLElement)?.closest?.('[data-pin]')) return;
            scheduleHide();
          }}
          style={{
            left: hoveredPin.pinX,
            transform: 'translateX(-50%)',
            ...(hoveredPin.pinTipY < CARD_H + CARD_GAP + 20
              ? { top: hoveredPin.pinTipY + CARD_GAP }
              : { top: hoveredPin.pinTipY - CARD_H - CARD_GAP }),
          }}
        >
          <NamedLink
            name="ListingPage"
            params={{ id: hoveredPin.id, slug: hoveredPin.slug }}
            className="block rounded-lg border border-border bg-background p-3 shadow-xl hover:border-primary transition-colors no-underline"
          >
            {hoveredPin.imgUrl && (
              <img
                src={hoveredPin.imgUrl}
                alt={hoveredPin.title}
                className="mb-2 h-24 w-full rounded object-cover"
              />
            )}
            {hoveredPin.formattedPrice && (
              <span className="mb-1 inline-block rounded bg-primary/15 px-2 py-0.5 text-xs font-bold text-primary">
                {hoveredPin.formattedPrice}
              </span>
            )}
            <p className="truncate text-sm font-bold">{hoveredPin.title}</p>
          </NamedLink>
        </div>
      )}

      <SearchMap
        reusableContainerClassName={css.mapContainer}
        rootClassName={css.mapContainer}
        bounds={boundsFromPins(pins)}
        location={{ search: '' }}
        listings={[]}
        onMapMoveEnd={() => {}}
        messages={intl.messages}
      />
    </section>
  );
};

export default MapView;
